import React, { useState } from 'react';
import { Button, Card, Badge, Row, Col, Alert } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const NewsBrandsManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleImageUpload = async (file) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({
        file: file,
        imageType: 'logo',
        status: true,
      });

      await homepageApi.addItemToSection(section.sectionKey, payload);
      toastSuccess('Brand logo added successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to add brand logo');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('Brand logo deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete brand logo');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(section.sectionKey, itemId, payload);
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, { items: updatedItems });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems();
    }
  };

  if (loading && !items.length) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                <i className="fa fa-home me-2"></i>Landing Page CMS
              </li>
              <li className="active">News & Brands Management</li>
            </ol>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              {/* Header */}
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className="mb-2">
                      <i className="fa fa-newspaper me-2 text-primary"></i>
                      News & Brands Management
                    </h3>
                    <p className="text-muted mb-2">
                      Upload brand logos for the &quot;As Featured In&quot; section
                    </p>
                    <div className="d-flex gap-2 align-items-center">
                      <Badge bg="primary" className="text-uppercase">List Section</Badge>
                      <Badge bg="secondary" className="font-monospace">{section.sectionKey}</Badge>
                      <Badge bg="info">{sortedItems.length} Brands</Badge>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => document.getElementById('brand-upload-input').click()}
                      disabled={loading}
                    >
                      <i className="fa fa-plus me-2"></i>Upload Brand Logo
                    </Button>
                    <input
                      type="file"
                      id="brand-upload-input"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleImageUpload(e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button variant="outline-secondary" size="sm" onClick={backToList}>
                      <i className="fa fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </div>
              </div>

              <div className="middle_main pt-4">
                {sortedItems.length === 0 ? (
                  <Card className="text-center py-5 border-0 bg-light">
                    <Card.Body>
                      <i className="fa fa-newspaper fa-5x text-muted mb-4 d-block"></i>
                      <h4 className="text-muted mb-2">No Brand Logos Yet</h4>
                      <p className="text-muted mb-4">
                        Upload brand logos to showcase media coverage and partnerships
                      </p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => document.getElementById('brand-upload-input').click()}
                      >
                        <i className="fa fa-plus me-2"></i>Upload Your First Logo
                      </Button>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <Alert variant="info" className="mb-4">
                      <i className="fa fa-info-circle me-2"></i>
                      <strong>Tip:</strong> Drag to reorder logos. Recommended size: 200x80px. Use transparent or white background for best results!
                    </Alert>

                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="news_brands">
                        {(provided, snapshot) => (
                          <Row
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{
                              backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'transparent',
                              padding: '10px',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s ease',
                            }}
                          >
                            {sortedItems.map((item, index) => (
                              <Draggable key={item._id} draggableId={item._id} index={index}>
                                {(provided, snapshot) => (
                                  <Col
                                    lg={3}
                                    md={4}
                                    sm={6}
                                    className="mb-4"
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <Card
                                      className="h-100 shadow-sm"
                                      style={{
                                        backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'white',
                                        transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                                        transition: 'all 0.2s ease',
                                      }}
                                    >
                                      {/* Card Header */}
                                      <Card.Header className="bg-light border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <div
                                              {...provided.dragHandleProps}
                                              style={{ cursor: 'grab' }}
                                            >
                                              <i className="fa fa-grip-vertical text-muted"></i>
                                            </div>
                                            <Badge bg="primary" pill>
                                              #{index + 1}
                                            </Badge>
                                          </div>
                                          <ReactSwitch
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            onChange={() => handleToggleStatus(item._id, item.status)}
                                            checked={item.status}
                                            height={20}
                                            width={40}
                                            onColor="#28a745"
                                            offColor="#6c757d"
                                          />
                                        </div>
                                      </Card.Header>

                                      {/* Card Body with Logo */}
                                      <Card.Body className="text-center d-flex align-items-center justify-content-center" style={{ minHeight: '150px', background: '#f8f9fa' }}>
                                        {item.logo?.location ? (
                                          <img
                                            src={item.logo.location}
                                            alt={`Brand ${index + 1}`}
                                            style={{
                                              maxWidth: '100%',
                                              maxHeight: '80px',
                                              objectFit: 'contain',
                                            }}
                                          />
                                        ) : (
                                          <div className="text-muted">
                                            <i className="fa fa-image fa-3x mb-2 d-block"></i>
                                            <small>No logo</small>
                                          </div>
                                        )}
                                      </Card.Body>

                                      {/* Card Footer */}
                                      <Card.Footer className="bg-white border-top">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            {item.status ? (
                                              <Badge bg="success">
                                                <i className="fa fa-eye me-1"></i>Visible
                                              </Badge>
                                            ) : (
                                              <Badge bg="secondary">
                                                <i className="fa fa-eye-slash me-1"></i>Hidden
                                              </Badge>
                                            )}
                                          </div>
                                          <div>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => handleDelete(item._id)}
                                            >
                                              <i className="fa fa-trash"></i>
                                            </Button>
                                          </div>
                                        </div>
                                      </Card.Footer>
                                    </Card>
                                  </Col>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Row>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        name="Brand Logo"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="News & Brands"
      />
    </div>
  );
};

export default NewsBrandsManager;
